import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  TextField,
  MenuItem,
  Grid,
  Button,
  Tooltip,
  ButtonGroup,
  CircularProgress,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Send as SendIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Print as PrintIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import * as quoteApi from '../../api/quotes';
import { Quote, CreateQuoteDTO, SupplierRate } from '../../types/quote.types';
import SupplierSelectionModal from '../../components/Quote/SupplierSelectionModal';
import PricingConfirmationModal from '../../components/Quote/PricingConfirmationModal';
import ContractCreationModal from '../../components/Quote/ContractCreationModal';
import QuoteRejectionModal from '../../components/Quote/QuoteRejectionModal';
import * as contractApi from '../../api/contracts';
import QuoteList from '../../components/Quote/QuoteList';
import { parseApiError } from '../../utils/errorHandler';

interface QuoteListPageProps {
  clientId?: string;
  propertyId?: string;
}

const QuoteListPage: React.FC<QuoteListPageProps> = ({ clientId, propertyId }) => {
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [filteredQuotes, setFilteredQuotes] = useState<Quote[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  
  // Helper to display errors with snackbar
  const displayError = (error: any, fallbackMessage: string) => {
    const parsed = parseApiError(error);
    enqueueSnackbar(parsed.message || fallbackMessage, { variant: 'error' });
    if (parsed.fieldErrors) {
      Object.entries(parsed.fieldErrors).forEach(([field, message]) => {
        const formattedField = field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()).trim();
        enqueueSnackbar(`${formattedField}: ${message}`, { variant: 'error' });
      });
    }
  };
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [supplierSelectionModal, setSupplierSelectionModal] = useState<{
    open: boolean;
    quote: Quote | null;
  }>({ open: false, quote: null });
  const [pricingConfirmationModal, setPricingConfirmationModal] = useState<{
    open: boolean;
    quote: Quote | null;
    selectedSupplier: SupplierRate | null;
  }>({ open: false, quote: null, selectedSupplier: null });
  const [contractCreationModal, setContractCreationModal] = useState<{
    open: boolean;
    quote: Quote | null;
    selectedSupplier: SupplierRate | null;
  }>({ open: false, quote: null, selectedSupplier: null });
  const [rejectionModal, setRejectionModal] = useState<{
    open: boolean;
    quote: Quote | null;
  }>({ open: false, quote: null });
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  
  // Get URL parameters
  const urlClientId = searchParams.get('clientId');
  const urlPropertyId = searchParams.get('propertyId');
  const finalClientId = clientId || urlClientId;
  const finalPropertyId = propertyId || urlPropertyId;

  // Filter quotes based on search term
  useEffect(() => {
    if (!Array.isArray(quotes)) {
      setFilteredQuotes([]);
      return;
    }

    const filtered = quotes.filter(quote => {
      if (!quote) return false;
      const searchString = searchTerm.toLowerCase();
      return (
        quote.quoteRef?.toLowerCase().includes(searchString) ||
        quote.client?.companyName?.toLowerCase().includes(searchString) ||
        quote.property?.address?.toLowerCase().includes(searchString) ||
        quote.meterIdentifier?.toLowerCase().includes(searchString)
      );
    });
    setFilteredQuotes(filtered);
  }, [searchTerm, quotes]);

  useEffect(() => {
    fetchQuotes();
  }, [clientId, propertyId]);

  const fetchQuotes = async () => {
    try {
      setLoading(true);
      let quotes;
      
      if (propertyId) {
        quotes = await quoteApi.getPropertyQuotes(propertyId);
      } else if (clientId) {
        quotes = await quoteApi.getClientQuotes(clientId);
      } else {
        quotes = await quoteApi.getAllQuotes();
      }
      
      if (Array.isArray(quotes)) {
        setQuotes(quotes);
        setFilteredQuotes(quotes);
      } else {
        setQuotes([]);
        setFilteredQuotes([]);
      }
    } catch (error) {
      displayError(error, 'Failed to fetch quotes');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateQuote = () => {
    navigate('/utilities/quotes/new');
  };

  const handleViewQuote = (quoteId: string) => {
    navigate(`/utilities/quotes/${quoteId}/view`);
  };

  const handlePrintQuote = async (quoteId: string, clientName: string) => {
    try {
      await quoteApi.downloadQuotePdf(quoteId, clientName);
      enqueueSnackbar('Quote PDF downloaded successfully', { variant: 'success' });
    } catch (error) {
      console.error('Error downloading quote PDF:', error);
      displayError(error, 'Failed to download quote PDF');
    }
  };

  const handleSendQuote = async (quoteId: string) => {
    try {
      await quoteApi.sendQuote(quoteId);
      enqueueSnackbar('Quote sent successfully', { variant: 'success' });
    } catch (error) {
      displayError(error, 'Failed to send quote');
    }
  };

  const handleDelete = async (quoteId: string) => {
    if (!window.confirm('Are you sure you want to delete this quote?')) {
      return;
    }

    try {
      await quoteApi.deleteQuote(quoteId);
      enqueueSnackbar('Quote deleted successfully', { variant: 'success' });
      fetchQuotes();
    } catch (error) {
      displayError(error, 'Failed to delete quote');
    }
  };

  const handleAcceptQuote = async (quoteId: string) => {
    // Accept quote should trigger the supplier selection modal workflow
    try {
      const quote = await quoteApi.getQuote(quoteId);
      if (quote.supplierRates.length === 0) {
        enqueueSnackbar('No supplier rates available for this quote', { variant: 'error' });
        return;
      }
      setSupplierSelectionModal({ open: true, quote });
    } catch (error) {
      displayError(error, 'Failed to load quote details');
    }
  };

  const handleRejectQuote = async (quoteId: string) => {
    try {
      const quote = await quoteApi.getQuote(quoteId);
      setRejectionModal({ open: true, quote });
    } catch (error) {
      displayError(error, 'Failed to load quote details');
    }
  };

  const handleQuoteRejection = async (quoteId: string, rejectionReason: string) => {
    try {
      await quoteApi.updateQuoteStatus(quoteId, { 
        status: 'REJECTED',
        rejectionReason 
      });
      enqueueSnackbar('Quote rejected successfully', { variant: 'success' });
      setRejectionModal({ open: false, quote: null });
      fetchQuotes();
    } catch (error: any) {
      displayError(error, 'Failed to reject quote');
      throw error;
    }
  };

  const handleSelectQuote = async (quoteId: string) => {
    try {
      const quote = await quoteApi.getQuote(quoteId);
      if (quote.supplierRates.length === 0) {
        enqueueSnackbar('No supplier rates available for this quote', { variant: 'error' });
        return;
      }
      setSupplierSelectionModal({ open: true, quote });
    } catch (error) {
      displayError(error, 'Failed to load quote details');
    }
  };

  const handleSupplierSelected = (quote: Quote, selectedSupplier: SupplierRate) => {
    setSupplierSelectionModal({ open: false, quote: null });
    setPricingConfirmationModal({ open: true, quote, selectedSupplier });
  };

  const handlePricingConfirmed = async (quote: Quote, selectedSupplier: SupplierRate) => {
    try {
      // First select the supplier
      await quoteApi.selectQuoteSupplier(quote._id, selectedSupplier.supplier);
      // Then update the quote status to SELECTED
      await quoteApi.updateQuoteStatus(quote._id, { status: 'SELECTED' });
      
      enqueueSnackbar('Quote selected successfully', { variant: 'success' });
      setPricingConfirmationModal({ open: false, quote: null, selectedSupplier: null });
      fetchQuotes();
      
      // Show contract creation modal
      setContractCreationModal({ open: true, quote, selectedSupplier });
    } catch (error) {
      displayError(error, 'Failed to select quote');
    }
  };

  const handleContractCreation = async (contractData: any) => {
    const { quote, selectedSupplier } = contractCreationModal;
    if (!quote || !selectedSupplier) return;

    try {
      // Pass the status from contractData to the API
      const contract = await contractApi.createContractFromQuote(quote, selectedSupplier, contractData.status);
      enqueueSnackbar('Contract created successfully', { variant: 'success' });
      setContractCreationModal({ open: false, quote: null, selectedSupplier: null });
      
      // Navigate to the new contract
      navigate(`/utilities/contracts/${contract._id}`);
    } catch (error: any) {
      displayError(error, 'Failed to create contract');
      throw error;
    }
  };

  const handleCompleteQuote = async (quoteId: string) => {
    try {
      const updatedQuote = await quoteApi.updateQuoteStatus(quoteId, { status: 'COMPLETED' });
      enqueueSnackbar('Quote completed successfully', { variant: 'success' });
      fetchQuotes();
    } catch (error) {
      displayError(error, 'Failed to complete quote');
    }
  };

  const getPageTitle = () => {
    if (propertyId) {
      return 'Property Quotes';
    } else if (clientId) {
      return 'Client Quotes';
    }
    return 'All Quotes';
  };

  const getQuoteActions = (quote: Quote) => {
    const actions = [];

    // View action - always available
    actions.push(
      <Tooltip title="View Quote" key="view">
        <IconButton 
          size="small" 
          onClick={() => handleViewQuote(quote._id)}
        >
          <EditIcon />
        </IconButton>
      </Tooltip>
    );

    // Status-specific actions
    if (quote.status === 'DRAFT') {
      actions.push(
        <Tooltip title="Send Quote" key="send">
          <IconButton 
            size="small" 
            onClick={() => handleSendQuote(quote._id)}
          >
            <SendIcon />
          </IconButton>
        </Tooltip>
      );
    }

    if (quote.status === 'ISSUED') {
      actions.push(
        <Tooltip title="Select Quote" key="select">
          <IconButton 
            size="small" 
            onClick={() => handleSelectQuote(quote._id)}
          >
            <CheckIcon />
          </IconButton>
        </Tooltip>,
        <Tooltip title="Reject Quote" key="reject">
          <IconButton 
            size="small" 
            onClick={() => handleRejectQuote(quote._id)}
          >
            <CloseIcon />
          </IconButton>
        </Tooltip>
      );
    }

    if (quote.status === 'SELECTED') {
      actions.push(
        <Tooltip title="Complete Quote" key="complete">
          <IconButton 
            size="small" 
            onClick={() => handleCompleteQuote(quote._id)}
          >
            <CheckIcon />
          </IconButton>
        </Tooltip>
      );
    }

    // Delete action - available for non-completed quotes
    if (quote.status !== 'COMPLETED') {
      actions.push(
        <Tooltip title="Delete Quote" key="delete">
          <IconButton 
            size="small" 
            onClick={() => handleDelete(quote._id)}
          >
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      );
    }

    // Download PDF action - available for all quotes
    actions.push(
      <Tooltip title="Download PDF" key="download">
        <IconButton 
          size="small" 
          onClick={() => handlePrintQuote(quote._id, quote.client?.companyName || 'Quote')}
        >
          <PrintIcon />
        </IconButton>
      </Tooltip>
    );

    return (
      <ButtonGroup size="small">
        {actions}
      </ButtonGroup>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" fontWeight="bold">
          {getPageTitle()}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleCreateQuote}
        >
          Create Quote
        </Button>
      </Box>

      <QuoteList
        quotes={quotes}
        loading={loading}
        onViewQuote={handleViewQuote}
        onPrintQuote={(quoteId) => {
          // Find the quote to get the client name
          const quote = quotes.find(q => q._id === quoteId);
          if (quote) {
            handlePrintQuote(quoteId, quote.client?.companyName || 'Quote');
          } else {
            // Fallback if quote not found
            handlePrintQuote(quoteId, 'Quote');
          }
        }}
        onSendQuote={handleSendQuote}
        onDeleteQuote={handleDelete}
        onAcceptQuote={handleAcceptQuote}
        onRejectQuote={handleRejectQuote}
        showClientName={!clientId}
        showPropertyName={!propertyId}
      />

      {/* Supplier Selection Modal */}
      <SupplierSelectionModal
        open={supplierSelectionModal.open}
        onClose={() => setSupplierSelectionModal({ open: false, quote: null })}
        quote={supplierSelectionModal.quote}
        onSelectSupplier={(selectedSupplier) => {
          if (supplierSelectionModal.quote) {
            handleSupplierSelected(supplierSelectionModal.quote, selectedSupplier);
          }
        }}
      />

      {/* Pricing Confirmation Modal */}
      <PricingConfirmationModal
        open={pricingConfirmationModal.open}
        onClose={() => setPricingConfirmationModal({ open: false, quote: null, selectedSupplier: null })}
        quote={pricingConfirmationModal.quote}
        selectedSupplier={pricingConfirmationModal.selectedSupplier}
        onConfirm={handlePricingConfirmed}
      />

      {/* Contract Creation Modal */}
      <ContractCreationModal
        open={contractCreationModal.open}
        onClose={() => setContractCreationModal({ open: false, quote: null, selectedSupplier: null })}
        quote={contractCreationModal.quote}
        selectedSupplier={contractCreationModal.selectedSupplier}
        onCreateContract={handleContractCreation}
      />

      {/* Quote Rejection Modal */}
      <QuoteRejectionModal
        open={rejectionModal.open}
        onClose={() => setRejectionModal({ open: false, quote: null })}
        quote={rejectionModal.quote}
        onReject={handleQuoteRejection}
      />
    </Box>
  );
};

export default QuoteListPage;
